import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, TrendingUp, Users, HeartHandshake, Mail, ShieldCheck, Infinity as InfinityIcon } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const reveal = {
    initial: { opacity: 0, y: 32 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.9, ease: [0.76, 0, 0.24, 1] },
};

const PILLARS = [
    {
        icon: HeartHandshake,
        title: "Emotional, universal problem",
        body: "Every family loses its stories. Photos scatter across devices, voice notes expire, and the people who remember pass away. The pain is universal — and nobody has built the permanent, private home for it.",
    },
    {
        icon: ShieldCheck,
        title: "Privacy as the moat",
        body: "Families are actively leaving ad-driven platforms. Ago GenApp is invite-only, encrypted, and never sells data — a trust position the incumbents structurally cannot copy.",
    },
    {
        icon: InfinityIcon,
        title: "Built for decades, not quarters",
        body: "A memory archive compounds: every photo added makes leaving harder and inviting the next generation easier. Retention is the product.",
    },
];

export default function Investors() {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        axios.get(`${API}/support/stats`).then((r) => setStats(r.data)).catch(() => {});
    }, []);

    return (
        <main data-testid="investors-page" className="min-h-screen bg-cream text-navy">
            <header className="border-b border-warmgray bg-cream/80 backdrop-blur-xl">
                <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-5">
                    <Link to="/" data-testid="investors-logo" aria-label="Ago GenApp home">
                        <img src="/logo.png" alt="Ago GenApp" className="h-10 w-auto" />
                    </Link>
                    <Link
                        to="/"
                        data-testid="investors-back-home"
                        className="flex items-center gap-2 rounded-full border-2 border-navy px-5 py-2 text-sm font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5"
                    >
                        <ArrowLeft size={15} /> Back Home
                    </Link>
                </div>
            </header>

            <div className="mx-auto max-w-5xl px-6 py-16 md:py-24">
                <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}>
                    <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.35em] text-[#C98A94]">For Investors</p>
                    <h1 data-testid="investors-heading" className="max-w-3xl font-display text-4xl leading-tight tracking-tight md:text-6xl">
                        The family memory economy is broken. <span className="italic">We’re fixing it.</span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-navy md:text-lg">
                        Ago GenApp is a privacy-first home for multi-generational family memories — photos, videos, voice
                        recordings, and written stories, organized into one living timeline that families hand down. We’re
                        community-funded, pre-seed, and opening our private beta in waves throughout 2026.
                    </p>
                </motion.div>

                <motion.div {...reveal} className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-3">
                    {[
                        {
                            label: "Raised from families",
                            value: stats ? `$${stats.total_raised.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "—",
                            sub: `of a $${(stats?.goal ?? 25000).toLocaleString()} community goal`,
                            testId: "investor-stat-raised",
                        },
                        {
                            label: "Founding supporters",
                            value: stats ? stats.supporter_count : "—",
                            sub: "real payments, live on Stripe",
                            testId: "investor-stat-supporters",
                        },
                        {
                            label: "Waitlist members",
                            value: stats ? stats.waitlist_count : "—",
                            sub: "families waiting for beta access",
                            testId: "investor-stat-waitlist",
                        },
                    ].map((s) => (
                        <div key={s.label} data-testid={s.testId} className="rounded-3xl border-2 border-warmgray bg-ivory p-7">
                            <div className="flex items-center gap-2 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-[#C98A94]">
                                <TrendingUp size={13} /> {s.label}
                            </div>
                            <p className="mt-3 font-display text-4xl">{s.value}</p>
                            <p className="mt-2 text-sm text-navy">{s.sub}</p>
                        </div>
                    ))}
                </motion.div>

                <motion.div {...reveal} className="mt-16">
                    <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.35em] text-[#C98A94]">Why now</p>
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                        {PILLARS.map((p) => (
                            <div key={p.title} className="rounded-3xl border-2 border-warmgray bg-ivory p-7">
                                <p.icon size={22} className="text-lavender" />
                                <h2 className="mt-4 font-display text-xl">{p.title}</h2>
                                <p className="mt-3 text-sm leading-relaxed text-navy">{p.body}</p>
                            </div>
                        ))}
                    </div>
                </motion.div>

                <motion.div {...reveal} className="mt-16 rounded-3xl border-2 border-warmgray bg-ivory p-8 md:p-10">
                    <p className="font-mono text-xs font-semibold uppercase tracking-[0.35em] text-[#C98A94]">The model</p>
                    <div className="mt-4 grid grid-cols-1 gap-8 md:grid-cols-2">
                        <div>
                            <h2 className="font-display text-2xl">Community-funded today, subscription tomorrow.</h2>
                            <p className="mt-3 text-sm leading-relaxed text-navy">
                                Founding supporters ($10–$100+ one-time, $10–$100 monthly) fund the build and prove willingness
                                to pay before launch. The product converts to Premium subscriptions at beta — supporters
                                already hold 12-month and Lifetime Premium tiers.
                            </p>
                        </div>
                        <ul className="space-y-3 text-sm text-navy">
                            {[
                                "Live Stripe payments — every dollar on this page is real",
                                "Organic loop: supporters invite family via personal invite links",
                                "Waitlist converts in waves — scarcity by design",
                            ].map((t) => (
                                <li key={t} className="flex items-start gap-3">
                                    <span className="mt-1.5 block h-1.5 w-1.5 shrink-0 rotate-45 bg-gradient-to-br from-lavender to-peach" />
                                    {t}
                                </li>
                            ))}
                        </ul>
                    </div>
                </motion.div>

                <motion.div {...reveal} className="mt-16 rounded-3xl bg-navy p-8 text-center text-cream md:p-12">
                    <Users size={26} className="mx-auto text-peach" />
                    <h2 className="mt-4 font-display text-2xl md:text-3xl">Let’s talk.</h2>
                    <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-cream/80">
                        We’re having early conversations with investors who believe privacy-first consumer products can win.
                        If that’s you, we’d love to show you the roadmap and the numbers behind this page.
                    </p>
                    <a
                        data-testid="investor-contact-button"
                        href="mailto:hello@agogenapp.com?subject=Investor%20conversation%20—%20Ago%20GenApp"
                        className="btn-gradient mt-7 inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
                    >
                        <Mail size={15} /> hello@agogenapp.com
                    </a>
                    <a
                        data-testid="investor-deck-button"
                        href="/ago-genapp-pitch-deck.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-7 ml-3 inline-flex items-center gap-2 rounded-full border-2 border-cream/60 px-8 py-4 text-sm font-semibold text-cream transition-transform duration-300 hover:-translate-y-0.5"
                    >
                        Download Pitch Deck (PDF)
                    </a>
                </motion.div>
            </div>
        </main>
    );
}
